import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { commentActions, commentThreadKey, httpRequestsOnSuccessActions } from 'store';
import { CREATE_COMMENT, GET_COMMENTS } from '../../store/comments/comment.type';
import { FindLoad, FindSuccess, Images } from 'utils';
import { Loader } from '../loader';
import { CommentEditor } from './commentEditor';
import { CommentEditorAi } from './commentEditorAi';
import { CommentItem } from './commentItem';
import { buildCommentTree } from './commentUtils';
import { CloseButton } from '../buttons';

const IconTabFile = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
    </svg>
);

/**
 * Comments — a threaded, chat-style comment panel that mounts against one or
 * more resources. Mirrors the <Notes> contract (id + model) but adds threading,
 * @mentions, attachments and a resource tab strip.
 *
 * Usage — single resource (like <Notes>):
 *   <Comments resource={commentModels.Claim} resourceId={claim.id} label="Claim" />
 *
 * Usage — multi-resource tabs (Claim + its Bills):
 *   <Comments resources={[
 *       { onModel: commentModels.Claim, resourceId: claim.id, label: 'Claim' },
 *       { onModel: commentModels.Bill,  resourceId: bill.id,  label: 'Bill 1' },
 *   ]} />
 *
 * Rendered standalone, or inside <CommentsPopper> as a floating panel.
 */
export const Comments = ({
    resource,
    resourceId,
    label,
    resources,
    width = 525,
    height,
    title = 'Comments',
    onClose,
    attachmentPurpose,
    // Opt-in AI "Improve" on the composer — see <CommentEditorAi>/useCommentImprove.
    enableAiImprove = false,
    fromDetail,
    afterSuccess,
    // Off for threads shared outside the org (e.g. the wishlist): @mentions
    // search this org's staff, which the other side of the thread is not in.
    enableMentions = true,
    // Set when the panel is mounted inside the primary modal — see CommentItem.
    confirmInSecondaryModal = false,
}) => {
    const dispatch = useDispatch();

    const resourceList = useMemo(() => {
        if (Array.isArray(resources) && resources.length) return resources.filter((r) => r?.onModel && r?.resourceId);
        if (resource && resourceId) return [{ onModel: resource, resourceId, label: label || 'Comments' }];
        return [];
    }, [resources, resource, resourceId, label]);

    const [activeIndex, setActiveIndex] = useState(0);
    const active = resourceList[activeIndex] || resourceList[0];

    const key = active ? commentThreadKey(active.onModel, active.resourceId) : null;
    const thread = useSelector((state) => (key ? state.comments.threads[key] : null));
    const loading = FindLoad(GET_COMMENTS);
    const createLoading = FindLoad(CREATE_COMMENT);
    const createSuccess = FindSuccess(CREATE_COMMENT);

    const rootSubmitted = useRef(false);
    const [rootReset, setRootReset] = useState(0);

    const requestParams = active ? { onModel: active.onModel, resourceId: active.resourceId, params: { flat: true } } : null;

    // Load the active thread + seed the mention list.
    useEffect(() => {
        if (!active) return;
        dispatch(commentActions.getComments(active.onModel, active.resourceId, { flat: true }));
    }, [active?.onModel, active?.resourceId]);

    // Warm the first page of the mention list so the popup shows instantly on `@`.
    useEffect(() => {
        if (enableMentions) dispatch(commentActions.getMentionStaff({ search: '', page: 1 }));
    }, []);

    // Clear the root composer once our own top-level comment lands.
    useEffect(() => {
        if (createSuccess?.length && rootSubmitted.current) {
            if (afterSuccess) {
                afterSuccess();
            }
            rootSubmitted.current = false;
            setRootReset((n) => n + 1);
            dispatch(httpRequestsOnSuccessActions.removeSuccess(CREATE_COMMENT));
        }
    }, [createSuccess]);

    const tree = useMemo(() => buildCommentTree(thread?.data || []), [thread]);

    const submitRoot = (text, attachments) => {
        if (!active) return;
        rootSubmitted.current = true;
        dispatch(
            commentActions.createComment({ onModel: active.onModel, resourceId: active.resourceId, text, attachments }, requestParams)
        );
    };

    if (!active) {
        return (
            <div className="comments-panel" style={{ width }}>
                <div className="comments-empty">No resource to comment on.</div>
            </div>
        );
    }

    // AI improve mounts a socket listener, so only use the AI-wired composer
    // when the caller opted in.
    const RootEditor = enableAiImprove ? CommentEditorAi : CommentEditor;

    return (
        <div className="comments-panel" style={{ width, height }}>
            {!fromDetail && (
                <div className="comments-header">
                    <div className="comments-header-title">
                        <img src={Images.commentChatIcon} alt="commentChatIcon" />
                        <span>{title}</span>
                    </div>
                    {onClose && <CloseButton handleCLic={onClose} />}
                </div>
            )}

            {resourceList.length > 1 && (
                <div className="comments-tabs">
                    {resourceList.map((r, i) => (
                        <button
                            key={commentThreadKey(r.onModel, r.resourceId)}
                            type="button"
                            className={`comments-tab ${i === activeIndex ? 'is-active' : ''}`}
                            onClick={() => setActiveIndex(i)}>
                            {i > 0 && <IconTabFile />}
                            <span>{r.label || r.onModel}</span>
                        </button>
                    ))}
                </div>
            )}

            <div className="comments-body">
                {loading?.length ? (
                    <div className="comments-loader">
                        <Loader height={300} />
                    </div>
                ) : tree.length === 0 ? (
                    <div className="comments-empty">
                        <div>
                            <img src={Images.commentChatIcon} alt="commentChatIcon" />
                        </div>
                        <p>No comments yet</p>
                        <span>Start the conversation below.</span>
                    </div>
                ) : (
                    tree.map((comment) => (
                        <CommentItem
                            key={comment.id}
                            comment={comment}
                            requestParams={requestParams}
                            enableMentions={enableMentions}
                            attachmentPurpose={attachmentPurpose}
                            confirmInSecondaryModal={confirmInSecondaryModal}
                        />
                    ))
                )}
            </div>

            <div className="comments-footer">
                <RootEditor
                    placeholder="Write a comment..."
                    submitting={createLoading?.length}
                    resetSignal={rootReset}
                    onSubmit={submitRoot}
                    attachmentPurpose={attachmentPurpose}
                    enableMentions={enableMentions}
                />
            </div>
        </div>
    );
};
